import React, { useEffect, useState } from 'react';
import { useKanban } from '../context/KanbanContext';
import { TAMANIO_LABELS } from '../types';
import { toast } from '../lib/toast';
import { EmptyState } from './EmptyState';

interface BacklogPanelProps {
    onClose: () => void;
}

export const BacklogPanel: React.FC<BacklogPanelProps> = ({ onClose }) => {
    const { backlogAntiguo, fetchBacklog, archivarTarea } = useKanban();
    const [loading, setLoading] = useState(true);
    const [archivandoId, setArchivandoId] = useState<number | null>(null);

    useEffect(() => {
        (async () => {
            setLoading(true);
            await fetchBacklog();
            setLoading(false);
        })();
    }, [fetchBacklog]);

    const handleArchivar = async (tareaId: number) => {
        if (!await toast.confirm({
            title: '¿Archivar esta tarea?',
            description: 'Dejará de aparecer en el tablero y en el backlog.',
            confirmText: 'Archivar',
            type: 'warning'
        })) return;
        setArchivandoId(tareaId);
        await archivarTarea(tareaId);
        setArchivandoId(null);
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col transform transition-all"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-5 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-slate-500 to-slate-700 rounded-xl flex items-center justify-center text-white text-lg">
                            🗂️
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-slate-800">Backlog</h2>
                            <p className="text-xs text-slate-500">Ordenado por antigüedad — revisa y poda tareas obsoletas</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="p-5 overflow-y-auto flex-1 space-y-3">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-32 gap-3">
                            <div className="relative">
                                <div className="w-10 h-10 border-4 border-slate-200 rounded-full"></div>
                                <div className="absolute inset-0 border-4 border-transparent border-t-blue-500 rounded-full animate-spin"></div>
                            </div>
                            <p className="text-slate-500 text-sm">Cargando backlog...</p>
                        </div>
                    ) : backlogAntiguo.length === 0 ? (
                        <EmptyState
                            icon="✨"
                            title="El backlog está vacío"
                            description="Las tareas en backlog aparecerán acá. Cuando lleguen nuevas solicitudes, las vas a ver acá esperando ser priorizadas."
                            variant="compact"
                        />
                    ) : (
                        backlogAntiguo.map(item => {
                            const esCritico = item.dias_en_backlog > 90;
                            const esAdvertencia = !esCritico && item.dias_en_backlog >= 30;

                            return (
                                <div
                                    key={item.id}
                                    className={`rounded-xl p-4 border ${
                                        esCritico
                                            ? 'bg-red-50 border-red-300'
                                            : esAdvertencia
                                                ? 'bg-amber-50 border-amber-200'
                                                : 'bg-slate-50 border-slate-200'
                                    }`}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                                <span
                                                    className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full text-white"
                                                    style={{ backgroundColor: item.cliente_color }}
                                                >
                                                    {item.empresa}
                                                </span>
                                                <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${
                                                    item.tamanio === 'XL' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'
                                                }`}>
                                                    {TAMANIO_LABELS[item.tamanio]}
                                                </span>
                                                <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${
                                                    esCritico
                                                        ? 'bg-red-200 text-red-800'
                                                        : esAdvertencia
                                                            ? 'bg-amber-200 text-amber-800'
                                                            : 'bg-slate-100 text-slate-500'
                                                }`}>
                                                    {esCritico ? '🚨 ARCHIVAR' : esAdvertencia ? '⚠️ REVISAR' : ''}
                                                </span>
                                                <span className={`text-[11px] font-semibold ${
                                                    esCritico ? 'text-red-600' : esAdvertencia ? 'text-amber-600' : 'text-slate-400'
                                                }`}>
                                                    {item.dias_en_backlog} día{item.dias_en_backlog === 1 ? '' : 's'} en backlog
                                                </span>
                                            </div>
                                            <p className="text-sm font-medium text-slate-700 truncate">{item.titulo}</p>
                                            <p className="text-xs text-slate-400 truncate">📁 {item.nombre_proyecto}</p>
                                            {item.tamanio === 'XL' && (
                                                <p className="text-[11px] text-red-600 mt-1">Tarea XL: considera dividirla en subtareas más pequeñas antes de tomarla.</p>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => handleArchivar(item.id)}
                                            disabled={archivandoId === item.id}
                                            className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors disabled:opacity-50 ${
                                                esCritico
                                                    ? 'border-red-300 text-red-700 hover:bg-red-100'
                                                    : 'border-slate-200 text-slate-600 hover:bg-slate-100'
                                            }`}
                                        >
                                            {archivandoId === item.id ? 'Archivando...' : 'Archivar'}
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                <div className="flex items-center justify-end p-5 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-100 transition-colors font-medium"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};
